import { useContext, useState } from "react";
import IKit_Builder_Option from "@models/IKit_Builder_Option";
import { OwAccordion } from "@components/shared/OwAccordion/OwAccordion";
import "./KitBuilderOption.scss";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import IKit_Builder_Option_ItemUI from "@models/IKit_Builder_Option_ItemUI";
import { KitBuilderSubItem } from "../KitBuilderSubItem/KitBuilderSubItem";
import { v4 } from "uuid";
import SupplierContext from "@contexts/SupplierContext";
import HelperService from "@services/HelperService";
import IKit_Builder_Option_Item_SelectionUI from "@models/IKit_Builder_Option_Item_SelectionUI";
import OwButton from "@components/shared/OwButton/OwButton";
import OwSelectOption from "@components/shared/OwSelectOption/OwSelectOption";
import { convertDimension, getDimensionUnitAbbreviation } from "@utils/DimensionConversion";
import DimensionUnitsEnum from "@models/DimensionUnitsEnum";

// The length entry toggle only offers meters/millimeters; storage stays in the item's dimension unit.
const ENTRY_UNITS = [DimensionUnitsEnum.METERS, DimensionUnitsEnum.MILLIMETERS];

// Default entry unit: the team works in meters, so start there even though we store mm.
const DEFAULT_ENTRY_UNIT = DimensionUnitsEnum.METERS;

// Kill floating-point noise from unit conversion (e.g. 1.5 m -> 1500.0000000000002 mm) while keeping sub-mm precision.
const roundDim = (value: number): number => Math.round(value * 1e6) / 1e6;

type Props = {
    option: IKit_Builder_Option;
    updateFn: (option: IKit_Builder_Option, itemSelections: IKit_Builder_Option_Item_SelectionUI[]) => void;
    showPrice: boolean;
    priceGroupName?: string;
    /**
     * Pre-existing selections (qty/length per line) used to seed the rows when re-opening the modal
     * to edit an existing kit. Sub-options are not seeded (not recoverable from an existing line).
     */
    initialSelections?: { optionItem: { id?: number }; qty?: number; length?: number }[];
};

type SelectionRow = {
    rowId: string;
    selectedItem: IKit_Builder_Option_ItemUI | null;
    selectedSubItem: IKit_Builder_Option_ItemUI | null;
    qty: number;
    /**
     * Per-line length for a length-priced component, stored in the item's dimension unit
     * (mm for Carl Stahl cable) and flows to the line's `depth`. The user may ENTER it in
     * meters or millimeters via `lengthUnit`; the entered value is converted to the item's
     * unit before it lands here so the pricing formula and PO output stay in mm.
     */
    length?: number;
    /** Unit the operator is entering in (meters or millimeters). Display-only; storage is `length` in the item's unit. */
    lengthUnit?: DimensionUnitsEnum;
    /** Raw string shown in the length field, expressed in `lengthUnit`. Kept separate so mid-typing values aren't reformatted. */
    lengthInput?: string;
};

export const KitBuilderOption = (props: Props) => {
    const supplierModel = useContext(SupplierContext);

    // Seed rows from any pre-existing selections (edit flow), else a single blank row.
    const buildInitialRows = (): SelectionRow[] => {
        const initial = props.initialSelections || [];

        if (!initial.length) {
            return [{ rowId: v4(), selectedItem: null, selectedSubItem: null, qty: 1 }];
        }

        const items = props.option.Kit_Builder_Option_Items || [];

        return initial.map((sel) => {
            const selectedItem = (items.find((item) => item.id === sel.optionItem?.id) || null) as IKit_Builder_Option_ItemUI | null;
            const itemUnit = selectedItem?.Catalog_Item?.dimension_unit_id;

            return {
                rowId: v4(),
                selectedItem,
                selectedSubItem: null,
                qty: sel.qty || 1,
                length: sel.length,
                lengthUnit: DEFAULT_ENTRY_UNIT,
                // Stored length is in the item's unit (mm); re-express it in the default entry unit for display.
                lengthInput: sel.length != null ? String(roundDim(convertDimension(sel.length, itemUnit, DEFAULT_ENTRY_UNIT))) : undefined,
            };
        });
    };

    const [selectionRows, setSelectionRows] = useState<SelectionRow[]>(buildInitialRows);
    // Start expanded when re-opening with existing selections so the operator sees what is chosen.
    const [isOpen, setIsOpen] = useState(!!props.initialSelections?.length);

    // Calculate total selected across all rows
    const totalSelected = selectionRows.reduce((total, row) => {
        return total + (row.selectedItem ? row.qty : 0);
    }, 0);

    // Get available options for dropdown (filter out selected if allow_repeats is false)
    const getAvailableOptions = (currentRowId: string) => {
        const allOptions = props.option.Kit_Builder_Option_Items || [];

        if (props.option.allow_repeats) {
            return allOptions;
        }

        // Filter out items already selected in other rows
        const selectedItemIds = selectionRows
            .filter((row) => row.rowId !== currentRowId && row.selectedItem)
            .map((row) => row.selectedItem!.id);

        const filteredOptions = allOptions.filter((item) => item.id !== undefined && !selectedItemIds.includes(item.id));

        // Always ensure the currently selected item for this row is included
        const currentRow = selectionRows.find((row) => row.rowId === currentRowId);
        if (currentRow?.selectedItem && !filteredOptions.find((item) => item.id === currentRow.selectedItem!.id)) {
            filteredOptions.push(currentRow.selectedItem);
        }

        return filteredOptions;
    };

    const outOf = props.option.max_allowed ? (
        <div>
            {totalSelected}
            <OwLabel title={"of"} className={"ms-2 me-2"} isLowercase={true} />
            {props.option.max_allowed}
            {props.option.min_required ? <OwLabel title={"required"} className={"mx-2 text-danger"} /> : null}
        </div>
    ) : (
        <div>
            {totalSelected}
            <OwLabel title={"selected"} className={"ms-2"} isLowercase={true} />
        </div>
    );

    const titleElem = (
        <div className={"d-flex align-items-center"}>
            <h6 className={"ms-0 mt-0 mb-0 me-2 text-underline"}>
                <b>{props.option.name}</b>
            </h6>
            {outOf}
        </div>
    );

    // Handle item selection from dropdown
    const handleItemSelect = (rowId: string, selectedItem: IKit_Builder_Option_ItemUI | null) => {
        const updatedRows = selectionRows.map((row) => {
            if (row.rowId === rowId) {
                return {
                    ...row,
                    selectedItem,
                    selectedSubItem: null, // Reset sub-item when main item changes
                    qty: selectedItem ? row.qty : 1,
                };
            }
            return row;
        });

        setSelectionRows(updatedRows);
        updateParent(updatedRows);
    };

    // Handle quantity change for a row
    const handleQuantityChange = (rowId: string, delta: number) => {
        const updatedRows = selectionRows.map((row) => {
            if (row.rowId === rowId) {
                const newQty = Math.max(1, row.qty + delta);

                // Check if total would exceed max_allowed
                const otherRowsTotal = selectionRows.filter((r) => r.rowId !== rowId && r.selectedItem).reduce((sum, r) => sum + r.qty, 0);

                if (props.option.max_allowed > 0 && otherRowsTotal + newQty > props.option.max_allowed) {
                    return row; // Don't update if it would exceed the limit
                }

                return { ...row, qty: newQty };
            }
            return row;
        });

        setSelectionRows(updatedRows);
        updateParent(updatedRows);
    };

    // Handle length change for a row. The operator enters in `row.lengthUnit` (m or mm); we convert
    // to the item's dimension unit (mm) for storage so the pricing formula and PO output stay in mm.
    const handleLengthChange = (rowId: string, rawValue: string) => {
        const updatedRows = selectionRows.map((row) => {
            if (row.rowId === rowId) {
                const entryUnit = row.lengthUnit ?? DEFAULT_ENTRY_UNIT;
                const itemUnit = row.selectedItem?.Catalog_Item?.dimension_unit_id;
                const parsed = rawValue === "" ? undefined : Number(rawValue);
                const length = parsed !== undefined && !isNaN(parsed) ? roundDim(convertDimension(parsed, entryUnit, itemUnit)) : undefined;

                return { ...row, lengthInput: rawValue, length };
            }
            return row;
        });

        setSelectionRows(updatedRows);
        updateParent(updatedRows);
    };

    // Toggle the entry unit (m <-> mm). The stored `length` (item's unit) is unchanged -- only the
    // displayed value is re-expressed -- so no parent update is needed.
    const handleLengthUnitChange = (rowId: string, newUnit: DimensionUnitsEnum) => {
        const updatedRows = selectionRows.map((row) => {
            if (row.rowId === rowId) {
                const itemUnit = row.selectedItem?.Catalog_Item?.dimension_unit_id;
                const lengthInput =
                    row.length != null ? String(roundDim(convertDimension(row.length, itemUnit, newUnit))) : row.lengthInput;

                return { ...row, lengthUnit: newUnit, lengthInput };
            }
            return row;
        });

        setSelectionRows(updatedRows);
    };

    // Add a new selection row
    const addRow = () => {
        setSelectionRows([
            ...selectionRows,
            {
                rowId: v4(),
                selectedItem: null,
                selectedSubItem: null,
                qty: 1,
            },
        ]);
    };

    // Delete a selection row
    const deleteRow = (rowId: string) => {
        const updatedRows = selectionRows.filter((row) => row.rowId !== rowId);
        // Ensure at least one row remains
        if (updatedRows.length === 0) {
            updatedRows.push({
                rowId: v4(),
                selectedItem: null,
                selectedSubItem: null,
                qty: 1,
            });
        }
        setSelectionRows(updatedRows);
        updateParent(updatedRows);
    };

    // Handle sub-item selection
    const handleSubItemSelect = (rowId: string, subItem: IKit_Builder_Option_ItemUI) => {
        const updatedRows = selectionRows.map((row) => {
            if (row.rowId === rowId) {
                return { ...row, selectedSubItem: subItem };
            }
            return row;
        });

        setSelectionRows(updatedRows);
        updateParent(updatedRows);
    };

    // Convert row structure to parent's expected format
    const updateParent = (rows: SelectionRow[]) => {
        const itemSelections: IKit_Builder_Option_Item_SelectionUI[] = [];

        rows.filter((row) => row.selectedItem).forEach((row) => {
            // Create a single entry with the actual quantity
            itemSelections.push({
                id: row.selectedItem!.id,
                uuid: row.rowId,
                item: row.selectedItem!,
                selected: true,
                qty: row.qty, // Pass the actual quantity
                length: row.length, // Pass the per-line length for length-priced components
                selectedSubItem: row.selectedSubItem,
            });
        });

        props.updateFn(props.option, itemSelections);
    };

    // Check if we can add more rows
    const canAddRow = () => {
        // Don't show add button if only 1 total option is allowed
        if (props.option.max_allowed === 1) {
            return false;
        }

        // Check if there are available options
        const availableOptions = getAvailableOptions("");
        if (availableOptions.length === 0) return false;

        // Check max_allowed limit
        if (props.option.max_allowed > 0 && totalSelected >= props.option.max_allowed) {
            return false;
        }

        return true;
    };

    return (
        <div className={"KitBuilderOption mb-1"}>
            <OwAccordion
                className={"border-0"}
                leftElem={titleElem}
                chevronSide={"RIGHT"}
                fullWidthClick={true}
                openOverride={isOpen}
                titleClick={true}
                fn={() => {
                    setIsOpen(!isOpen);
                }}>
                <div className={"ms-4 pt-4"}>
                    {selectionRows.map((row) => {
                        const availableOptions = getAvailableOptions(row.rowId);
                        const showSubItems = row.selectedItem && row.selectedItem.Sub_Kit_Builder_Option_Items?.length;

                        return (
                            <div key={row.rowId} className={"mb-4"}>
                                <div className={"d-flex align-items-center gap-2"}>
                                    {/* Option Dropdown */}
                                    <div className={"flex-grow-1"}>
                                        <OwSelectOption
                                            options={availableOptions}
                                            valueProp="id"
                                            titleProp="Catalog_Item.name"
                                            titlePropTwo="Catalog_Item.description"
                                            titleRenderFn={(option) => {
                                                const name = option.Catalog_Item?.name || "";
                                                const desc = option.Catalog_Item?.description || "";
                                                const price =
                                                    props.showPrice &&
                                                    props.priceGroupName &&
                                                    option.Catalog_Item?.pricing?.[props.priceGroupName]
                                                        ? ` - ${HelperService.formatCurrency({
                                                              value: option.Catalog_Item.pricing[props.priceGroupName],
                                                              currency: "USD",
                                                              precision: supplierModel.pricing_precision || 2,
                                                          })}`
                                                        : "";
                                                return `${name} - ${desc}${price}`;
                                            }}
                                            selectedOption={row.selectedItem}
                                            selectedOptionProp="id"
                                            fn={(selectedOption: any) => handleItemSelect(row.rowId, selectedOption)}
                                            name={`kit_option_${props.option.id}_${row.rowId}`}
                                            emitObject={true}
                                            showBlankFirst={true}
                                            autoSelectSingleOption={false}
                                        />
                                    </div>

                                    {/* Quantity Controls */}
                                    {props.option.allow_repeats && row.selectedItem ? (
                                        <div className={"d-flex align-items-center"}>
                                            <OwButton
                                                disableDblClickPrevention={true}
                                                className={"btn-outline-secondary btn-sm"}
                                                fn={() => handleQuantityChange(row.rowId, -1)}
                                                disabled={row.qty <= 1}>
                                                -
                                            </OwButton>
                                            <span className={"mx-2 fw-bold"}>{row.qty}</span>
                                            <OwButton
                                                disableDblClickPrevention={true}
                                                className={"btn-outline-secondary btn-sm"}
                                                fn={() => handleQuantityChange(row.rowId, 1)}
                                                disabled={
                                                    props.option.max_allowed > 0 &&
                                                    totalSelected - row.qty + row.qty + 1 > props.option.max_allowed
                                                }>
                                                +
                                            </OwButton>
                                        </div>
                                    ) : null}

                                    {/* Length Input (only for length-priced / formula-priced components, e.g. cut-to-length cable) */}
                                    {row.selectedItem?.Catalog_Item?.pricing_formula_id ? (
                                        <div className={"position-relative"} style={{ minWidth: "8rem" }}>
                                            {/* Float the label above so the input aligns with the other row controls */}
                                            <div className={"position-absolute text-muted small"} style={{ bottom: "100%", left: 0 }}>
                                                <OwLabel title={supplierModel.length_for_depth ? "length" : "depth"} />
                                            </div>
                                            <div className={"input-group input-group-sm"}>
                                                <input
                                                    type={"number"}
                                                    inputMode={"decimal"}
                                                    min={0}
                                                    step={"any"}
                                                    className={"form-control form-control-sm"}
                                                    aria-label={`kit_option_${props.option.id}_${row.rowId}_length`}
                                                    value={row.lengthInput ?? ""}
                                                    onChange={(e) => handleLengthChange(row.rowId, e.target.value)}
                                                    onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                                />
                                                {ENTRY_UNITS.map((unit) => {
                                                    const active = (row.lengthUnit ?? DEFAULT_ENTRY_UNIT) === unit;

                                                    return (
                                                        <button
                                                            key={unit}
                                                            type={"button"}
                                                            className={"btn btn-sm " + (active ? "btn-secondary" : "btn-outline-secondary")}
                                                            aria-pressed={active}
                                                            aria-label={`kit_option_${props.option.id}_${row.rowId}_unit_${unit}`}
                                                            onClick={() => handleLengthUnitChange(row.rowId, unit)}>
                                                            {getDimensionUnitAbbreviation(unit)}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ) : null}

                                    {/* Delete Button */}
                                    {selectionRows.length > 1 || (selectionRows.length === 1 && !props.option.min_required) ? (
                                        <OwButton className={"btn-outline-danger btn-sm"} fn={() => deleteRow(row.rowId)}>
                                            <i className="fa fa-trash" />
                                        </OwButton>
                                    ) : null}
                                </div>

                                {/* Sub-items */}
                                {showSubItems && row.selectedItem ? (
                                    <div className={"ms-5 mt-2"}>
                                        <KitBuilderSubItem
                                            item={row.selectedItem}
                                            subSelectFn={(_, subItem) => handleSubItemSelect(row.rowId, subItem)}
                                            showPrice={props.showPrice}
                                            priceGroupName={props.priceGroupName}
                                        />
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}

                    {/* Add Row Button */}
                    {canAddRow() ? (
                        <div className={"mt-3"}>
                            <OwButton className={"btn-outline-primary btn-sm"} fn={addRow}>
                                <i className="fa fa-plus me-2" />
                                Add Another Option
                            </OwButton>
                        </div>
                    ) : null}
                </div>
            </OwAccordion>
        </div>
    );
};
